<template>
  <b-card v-if="render">
    <layout-loading v-if="itemLoading" />
    <b-form>
      <b-row>
        <b-col md="6">
          <b-form-group
            :label="$t('general.title')"
            label-for="article-category-title"
          >
            <b-form-input
              id="article-category-title"
              v-model="form.title"
              :state="validateState('title')"
            />
            <b-form-invalid
              id="input-title-feedback"
              :label="$t('general.title')"
              :validator="$v.form.title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.slug')"
            label-for="article-category-slug"
          >
            <b-form-input
              id="article-category-slug"
              v-model="form.slug"
              :state="validateState('slug')"
            />
            <b-form-invalid
              id="input-slug-feedback"
              :label="$t('general.slug')"
              :validator="$v.form.slug"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('general.priority')"
            label-for="article-category-sort"
          >
            <b-form-input
              id="article-category-sort"
              v-model.number="form.sort"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('general.studyTime')"
            label-for="article-study-time"
          >
            <b-form-input
              id="article-study-time"
              v-model="form.studyTime"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('general.language')"
            label-for="service-lang"
          >
            <languageSelector
              v-model="form.lang"
              @change-selected-language="changeSelectedLanguage"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('general.showOnMenu')"
            label-for="article-showOnMenu"
          >
            <b-form-checkbox
              id="article-showOnMenu"
              v-model="form.showOnMenu"
              class="mt-1"
              name="article-showOnMenu"
              switch
              inline
            />
            <b-form-invalid
              id="input-showOnMenu-feedback"
              :label="$t('general.showOnMenu')"
              :validator="$v.form.showOnMenu"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('general.waterMark')"
            label-for="article-watermark"
          >
            <b-form-checkbox
              id="article-watermark"
              v-model="form.watermark"
              class="mt-1"
              name="article-waterMark"
              switch
              inline
            />
            <b-form-invalid
              id="input-watermark-feedback"
              :label="$t('general.waterMark')"
              :validator="$v.form.watermark"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('general.tableOfContentShow')"
            label-for="article-tableOfContentShow"
          >
            <b-form-checkbox
              id="article-tableOfContentShow"
              v-model="form.showTableOfContent"
              class="mt-1"
              name="article-tableOfContentShow"
              switch
              inline
            />
            <b-form-invalid
              id="input-tableOfContentShow-feedback"
              :label="$t('general.tableOfContentShow')"
              :validator="$v.form.showTableOfContent"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.category')"
            label-for="article-category"
            :state="validateState('category')"
          >
            <v-select-custom
              id="article-category"
              v-model="form.category"
              :options="rootCategoryOptions"
            />
            <b-form-invalid
              id="input-category-feedback"
              :label="$t('general.category')"
              :validator="$v.form.category"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.parentCategory')"
            label-for="parent-category"
          >
            <v-select
              id="parent-category"
              v-model="form.parentCategory"
              dir="rtl"
              label="title"
              :options="categoryOptions"
              @search="fetchCategories(form.lang, $event, 'title')"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.approver')"
            label-for="article-approver"
          >
            <v-select
              id="article-approver"
              v-model="form.approver"
              label="firstName"
              dir="rtl"
              :options="approverOptions"
              @search="fetchApprovers(form.lang, $event)"
            >
              <template #selected-option="option">
                <v-select-option
                  :value="option.managerImageFileId"
                  :title="option.firstName + option.lastName"
                />
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <v-select-option
                  :value="option.managerImageFileId"
                  :title="option.firstName + option.lastName"
                />
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.writer')"
            label-for="article-writer"
            :state="validateState('writer')"
          >
            <v-select-custom
              id="article-writer"
              v-model="form.writer"
              :custom-props="{ label: 'fullName' }"
              :options="userOptions"
              @open="fetchUsersByRole({ role: 'writer' }, form.lang)"
              @search="fetchUsersByRole({role: 'writer', 'search' : $event}, form.lang)"
            />
            <b-form-invalid
              id="input-writerId-feedback"
              :label="$t('general.writer')"
              :validator="$v.form.writer"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.editor')"
            label-for="article-editor"
          >
            <v-select-custom
              id="article-editor"
              v-model="form.editor"
              :custom-props="{ label: 'fullName' }"
              :options="userOptions"
              @open="fetchUsersByRole({ role: 'editor' }, form.lang)"
              @search="fetchUsersByRole({role: 'editor', 'search' : $event}, form.lang)"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.metaTitle')"
            label-for="article-category-metaTitle"
          >
            <b-form-input
              id="article-category-metaTitle"
              v-model="form.metaTitle"
              :state="validateState('metaTitle')"
            />
            <b-form-invalid
              id="input-metaTitle-feedback"
              :label="$t('general.metaTitle')"
              :validator="$v.form.metaTitle"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.tags')"
            label-for="article-category-tag"
            :state="validateState('tags')"
          >
            <v-select-custom
              id="article-category-tag"
              v-model="form.tags"
              :custom-props="{ multiple: true, taggable: true }"
              :options="tags"
              @search="fetchTags(form.lang, $event)"
              @option:created="addNewTag(form.lang, $event)"
            />
            <b-form-invalid
              id="input-tags-feedback"
              :label="$t('general.tags')"
              :validator="$v.form.tags"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('articles.relatedArticles')"
            label-for="article-related-articles"
          >
            <v-select
              id="article-related-articles"
              v-model="form.relatedArticles"
              dir="rtl"
              label="title"
              multiple
              :options="articleOptions"
              @search="fetchArticles(form.lang, $event, 'title')"
            >
              <template #selected-option="option">
                <v-select-option
                  :value="option.articleThumbnailFileId"
                  :title="option.title"
                />
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <v-select-option
                  :value="option.articleThumbnailFileId"
                  :title="option.title"
                />
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.relatedServices')"
            label-for="article-related-services"
          >
            <v-select
              id="article-related-services"
              v-model="form.relatedServices"
              dir="rtl"
              label="title"
              multiple
              :options="serviceOptions"
              @search="fetchServices(form.lang, $event, 'title')"
            >
              <template #selected-option="option">
                <v-select-option
                  :value="option.serviceThumbnailFileId"
                  :title="option.title"
                />
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <v-select-option
                  :value="option.serviceThumbnailFileId"
                  :title="option.title"
                />
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.relatedQuestions')"
            label-for="article-related-questions"
          >
            <v-select
              id="article-related-questions"
              v-model="form.relatedQuestions"
              dir="rtl"
              label="title"
              multiple
              :options="questionOptions"
              @search="fetchQuestions(form.lang, $event, 'title')"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.relatedArticleCategories')"
            label-for="article-related-categories"
          >
            <v-select
              id="article-related-categories"
              v-model="form.relatedCategories"
              dir="rtl"
              label="title"
              multiple
              :options="categoryOptions"
              @search="fetchCategories(form.lang, $event, 'title')"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.keywords')"
            label-for="article-category-keywords"
          >
            <b-form-input
              id="article-category-keywords"
              v-model="form.keywords"
              :state="validateState('keywords')"
            />
            <b-form-invalid
              id="input-keywords-feedback"
              :label="$t('general.keywords')"
              :validator="$v.form.keywords"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.canonicalUrl')"
            label-for="article-category-canonicalUrl"
            class="mb-2"
          >
            <b-form-input
              id="article-category-canonicalUrl"
              v-model="form.canonicalUrl"
              :state="validateState('canonicalUrl')"
            />
            <b-form-invalid
              id="input-category-canonicalUrl"
              :label="$t('general.canonicalUrl')"
              :validator="$v.form.canonicalUrl"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-row class="">
            <b-col>
              <b-button
                variant="outline-primary"
                @click="addSourceItem"
              >
                {{ $t("general.addSourceItem") }}
              </b-button>
            </b-col>
          </b-row>
        </b-col>
        <b-col md="12">
          <b-row
            v-for="(source, index) in form.source"
            :key="index"
            class="mt-1 bg-booking p-1"
          >
            <b-col md="3">
              <b-form-group
                :label="$t('general.sourceName')"
                label-for="article-source-name"
              >
                <b-form-input
                  id="article-source-name"
                  v-model="source.sourceName"
                />
              </b-form-group>
            </b-col>
            <b-col md="7">
              <b-form-group
                :label="$t('general.sourceLink')"
                label-for="article-source-link"
              >
                <b-form-input
                  id="article-source-link"
                  v-model="source.sourceLink"
                />
              </b-form-group>
            </b-col>
            <b-col class="mt-2">
              <b-button
                variant="outline-success"
                @click="removeSourceItem(index)"
              >
                {{ $t("general.removeSourceItem") }}
              </b-button>
            </b-col>
          </b-row>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.description')"
            label-for="article-category-description"
          >
            <b-form-textarea
              id="article-category-description"
              v-model="form.description"
              :state="validateState('description')"
              rows="6"
              aria-describedby="input-description-feedback"
            />
            <b-form-invalid
              id="input-description-feedback"
              :label="$t('general.description')"
              :validator="$v.form.description"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="h2Tags && h2Tags.length != 0 && form.showTableOfContent"
          class="mb-2"
          cols="12"
        >
          <h3 class="text-center">
            {{ $t('articles.h2Tags') }}
          </h3>
          <hr>
          <table class="table table-bordered table-hover">
            <thead>
              <tr>
                <th style="width: 60px;">
                  {{ $t('general.row') }}
                </th>
                <th style="width: 80%">
                  {{ $t('articles.h2Text') }}
                </th>
                <th>{{ $t('articles.h2Id') }}</th>
                <!-- <th>{{ $t('articles.h2Op') }}</th> -->
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item,index) in h2Tags"
                :key="index"
              >
                <td>{{ ++index }}</td>
                <td>
                  <p v-if="!item.editable">
                    {{ item.text }}
                  </p>
                  <b-form-group
                    v-if="item.editable"
                    :label-for="item.id"
                  >
                    <b-form-input
                      :id="item.id"
                      v-model="newH2Text"
                    />
                  </b-form-group>
                </td>
                <td>{{ item.id }}</td>
                <!-- <td>
                  <feather-icon
                    icon="EditIcon"
                    @click="showEditable(index)"
                    size="18"
                    opacity=".6"
                    hover-opacity=".9"
                    class="hover-scale-up mr-1"
                  />
                </td> -->
              </tr>
            </tbody>
          </table>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <b-form-group
            label-for="article-category-content"
            class="mb-2"
          >
            <CkeditorFull
              id="article-category-content"
              ref="editor"
              v-model="form.content"
              :label="$t('general.content')"
              :state="validateState('content')"
            />
            <b-form-invalid
              id="input-content-feedback"
              :label="$t('general.content')"
              :validator="$v.form.content"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <b-file-input
            id="articleCategoryIcon"
            v-model="form.articleCategoryIcon"
            :label="$t('general.icon') + ' ' + $t('general.acceptImage')"
            :placeholder="$t('general.select')"
            :state="validateState('articleCategoryIcon')"
            :state-alt="validateState('articleCategoryIcon')"
            :alt.sync="form.articleCategoryIconAlt"
            :alt-validator="$v.form.articleCategoryIconAlt"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-article-category-icon-feedback"
                :label="$t('general.icon')"
                :validator="$v.form.articleCategoryIcon"
                :is-bootstrap="false"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <b-file-input
            id="articleCategoryThumbnail"
            v-model="form.articleCategoryThumbnail"
            :label="$t('general.thumbnail') + ' ' + $t('general.acceptImage')"
            :placeholder="$t('general.select')"
            :state="validateState('articleCategoryThumbnail')"
            :state-alt="validateState('articleCategoryThumbnail')"
            :alt.sync="form.articleCategoryThumbnailAlt"
            :alt-validator="$v.form.articleCategoryThumbnailAlt"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-article-category-thumbnail-feedback"
                :label="$t('general.cover')"
                :validator="$v.form.articleCategoryThumbnail"
                :is-bootstrap="false"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <b-file-input
            id="articleCategoryImage"
            v-model="form.articleCategoryImage"
            :label="$t('general.cover') + ' ' + $t('general.acceptImage')"
            :placeholder="$t('general.select')"
            :state="validateState('articleCategoryImage')"
            :state-alt="validateState('articleCategoryImage')"
            :alt.sync="form.articleCategoryImageAlt"
            :alt-validator="$v.form.articleCategoryImageAlt"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-article-category-cover-feedback"
                :label="$t('general.cover')"
                :validator="$v.form.articleCategoryImage"
                :is-bootstrap="false"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t("general.isSubmit") : $t("general.submit") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>
<script>
import {
  BButton,
  BCard,
  BCol,
  BForm,
  BFormGroup,
  BFormCheckbox,
  BFormInput,
  BFormTextarea,
  BRow,
} from 'bootstrap-vue'
import BFileInput from '@/components/BFileInput/index.vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import CkeditorFull from '@/components/CkeditorFull/index.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import Ripple from 'vue-ripple-directive'
import VSelectCustom from '@/components/VSelectCustom/index.vue'
import VSelectOption from '@/components/VSelectCustom/VSelectOption.vue'
import languageSelector from '@/components/language-selector.vue'
import vSelect from 'vue-select'
import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import useArticleCategoriesList from './useArticleCategoriesList'

export default {
  name: 'ArticleCategoryEdit',
  components: {
    BButton,
    BCard,
    BCol,
    BFileInput,
    BFormCheckbox,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BFormTextarea,
    BRow,
    CkeditorFull,
    LayoutLoading,
    VSelectCustom,
    VSelectOption,
    languageSelector,
    vSelect,
  },
  directives: {
    Ripple,
  },
  setup() {
    const {
      addNewTag,
      approverOptions,
      articleOptions,
      categoryOptions,
      checkSlugIsUnique,
      convertResource,
      createTagIdArray,
      editorOptions,
      errorManager,
      fetchApprovers,
      fetchArticles,
      fetchCategories,
      fetchItem,
      fetchQuestions,
      fetchServices,
      fetchTags,
      fetchUsersByRole,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      item,
      itemLoading,
      newTagIds,
      questionOptions,
      rootCategoryOptions,
      serviceOptions,
      tags,
      updateItem,
      writerOptions,
      userOptions,
    } = useArticleCategoriesList()

    return {
      addNewTag,
      approverOptions,
      articleOptions,
      checkSlugIsUnique,
      convertResource,
      createTagIdArray,
      fetchServices,
      fetchQuestions,
      fetchCategories,
      serviceOptions,
      categoryOptions,
      questionOptions,
      editorOptions,
      errorManager,
      fetchApprovers,
      fetchArticles,
      fetchItem,
      fetchTags,
      fetchUsersByRole,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      item,
      itemLoading,
      newTagIds,
      tags,
      updateItem,
      rootCategoryOptions,
      writerOptions,
      userOptions,
    }
  },
  data() {
    return {
      sources: [],
      searchT: '',
      h2Tags: [],
      newH2Text: '',
    }
  },
  validations() {
    return {
      form: {
        title: { required },
        metaTitle: { required },
        slug: { required, isUnique: () => this.isUniqueSlug },
        tags: { required },
        keywords: { minLength: minLength(3) },
        writer: { required },
        category: { required },
        parentCategory: {},
        description: { minLength: minLength(160), maxLength: maxLength(320) },
        content: { minLength: minLength(10) },
        articleCategoryIcon: { required },
        articleCategoryThumbnail: { required },
        articleCategoryImage: { required },
        articleCategoryIconAlt: { minLength: minLength(3) },
        articleCategoryThumbnailAlt: { minLength: minLength(3) },
        articleCategoryImageAlt: { minLength: minLength(3) },
        canonicalUrl: {},
      },
    }
  },
  computed: {
    itemId() {
      return this.$router.currentRoute.params.id
    },
    render() {
      return this.item ? this.item && Object.keys(this.item).length : true
    },
  },
  watch: {
    'form.slug': function () {
      this.checkSlugIsUnique(this.form.lang, this.form.slug)
    },
  },
  mounted() {
    this.fetchArticles(this.form.lang, this.searchT, '')
    this.fetchCategories(this.form.lang, ' ', 'title')
    setTimeout(() => {
      this.getH2Tag()
    }, 3000)
  },
  created() {
    this.fetchItem(this.itemId).then(() => {
      this.fetchApprovers()
      this.fetchUsersByRole({ role: 'writer' }).then(res => { this.writerOptions = res })
      this.fetchUsersByRole({ role: 'editor' }).then(res => { this.editorOptions = res })
      this.fetchTags(this.form.lang)
      this.fetchArticles(this.form.lang, this.searchT, 'title')
      this.fetchCategories(this.form.lang, '', '', 'null')
      this.fetchServices(this.form.lang)
      this.fetchQuestions(this.form.lang)
    })
  },
  methods: {
    addSourceItem() {
      if (this.form.source.length === 0) {
        this.form.source.push({ sourceName: '', sourceLink: '' })
      } else if (this.form.source[this.form.source.length - 1].clinic !== '') {
        this.form.source.push({ sourceName: '', sourceLink: '' })
      }
    },
    removeSourceItem(index) {
      this.form.source.splice(index, 1)
    },
    changeSelectedLanguage(selectedLanguage) {
      this.form = this.convertResource(this.item, selectedLanguage)
      this.fetchCategories(selectedLanguage, '', '', 'null')
      this.fetchTags(selectedLanguage)
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    getH2Tag() {
      const el = document.getElementById('article-category-content')
      if (el) {
        const allChilds = el.children
        const childArray = [...allChilds]
        for (let i = 0; i < childArray.length; i += 1) {
          if (childArray[i].localName === 'h2') {
            const data = {
              text: childArray[i].innerText,
              editable: false,
              id: `id-h2-${i}`,
            }
            childArray[i].setAttribute('id', `id-h2-${i}`)
            this.h2Tags.push(data)
          }
        }
      }
    },
    submitForm() {
      this.$refs.editor.sendValToForm()
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        setTimeout(() => document.getElementsByClassName('is-invalid')[0]
          .scrollIntoView({ behavior: 'smooth', block: 'center' }), 500)
        return
      }
      const uploadList = ['articleCategoryImage', 'articleCategoryThumbnail', 'articleCategoryIcon']
      this.handleUpload(this.form, uploadList).then(newForm => {
        this.sendData(newForm)
      })
    },
    sendData(form) {
      const data = {
        title: form.title,
        metaTitle: form.metaTitle,
        slug: form.slug,
        sort: form.sort,
        studyTime: form.studyTime,
        showOnMenu: form.showOnMenu,
        showTableOfContent: form.showTableOfContent,
        watermark: this.form.watermark != null ? this.form.watermark : false,
        source: this.form.source ? this.form.source.map(item => ({ sourceName: item.sourceName, sourceLink: item.sourceLink })) : [],
        parentId: form.parentCategory ? form.parentCategory._id : null,
        approverId: form.approver ? form.approver._id : null,
        writerId: form.writer ? form.writer._id : null,
        editorId: form.editor ? form.editor._id : null,
        relatedArticleIds: form.relatedArticles ? form.relatedArticles.map(item => item._id) : [],
        relatedQuestionIds: form.relatedQuestions ? form.relatedQuestions.map(item => item._id) : [],
        relatedServiceIds: form.relatedServices ? form.relatedServices.map(item => item._id) : [],
        relatedCategoryIds: form.relatedCategories ? form.relatedCategories.map(item => item._id) : [],
        keywords: form.keywords,
        description: form.description,
        content: form.content,
        tagIds: this.createTagIdArray(this.form.tags, this.newTagIds),
        articleCategoryImageFileId: form.articleCategoryImage ? form.articleCategoryImage._id : null,
        articleCategoryIconFileId: form.articleCategoryIcon ? form.articleCategoryIcon._id : null,
        articleCategoryThumbnailFileId: form.articleCategoryThumbnail ? form.articleCategoryThumbnail._id : null,
        mainParent: form.category ? form.category._id : null,
        canonicalUrl: form.canonicalUrl,

      }
      this.updateItem(this.itemId, data, this.form.lang)
    },
  },
}
</script>
